/**
 * ControlledInputs
 *
 * `CustomTextInput` / `CustomSingleSelect` は react-hook-form の
 * `register` / `control` 必須で、行ごとに動的な controlled state を扱うのには
 * 不向き。本ファイルでは、各行で `value` / `onChange` を直接渡せる薄い
 * wrapper を提供する。
 *
 * いずれもドロワー内のローカル state 用途なので、libs には入れず
 * InspectionCleaning/drawers 配下にのみ閉じる。
 */
import React from "react";
import { useForm } from "react-hook-form";
import {
  CustomTextInput,
  CustomSingleSelect,
} from "@ippon/shared-components";

// ──────────────────────────────────────────
// ControlledTextInput
// ──────────────────────────────────────────

export interface ControlledTextInputProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  name?: string;
}

/**
 * `CustomTextInput` を value/onChange で使うための wrapper。
 * 行ごとに極小の useForm を作り、register 経由で値を流し込む。
 */
export const ControlledTextInput: React.FC<ControlledTextInputProps> = ({
  value,
  onChange,
  label = "",
  placeholder,
  multiline,
  rows,
  disabled,
  name = "value",
}) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useForm<{ value: string }>({
    defaultValues: { value: value ?? "" },
  });

  // 外部 value 変化をフォームに反映
  React.useEffect(() => {
    setValue(name as "value", value ?? "");
  }, [value, setValue, name]);

  // register された onChange に upstream を flow させるため、
  // register の戻りの onChange を上書きする形で wrapper する。
  const reg = register(name as "value");
  const wrappedRegister = (_n: string) => ({
    ...reg,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      reg.onChange(e);
      onChange(e.target.value);
    },
  });

  return (
    <CustomTextInput
      label={label}
      name={name}
      register={wrappedRegister}
      errors={errors}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      disabled={disabled}
      hasSpacing={false}
      inputWidth="100%"
      inputWidthSp="100%"
    />
  );
};

// ──────────────────────────────────────────
// ControlledSingleSelect
// ──────────────────────────────────────────

export interface ControlledSingleSelectOption {
  value: string;
  label: string;
}

export interface ControlledSingleSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: ControlledSingleSelectOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
}

/**
 * `CustomSingleSelect` を value/onChange で使うための wrapper。
 */
export const ControlledSingleSelect: React.FC<ControlledSingleSelectProps> = ({
  value,
  onChange,
  options,
  label = "",
  placeholder,
  disabled,
  name = "value",
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<{ value: string }>({
    defaultValues: { value: value ?? "" },
  });

  React.useEffect(() => {
    setValue(name as "value", value ?? "");
  }, [value, setValue, name]);

  return (
    <CustomSingleSelect
      label={label}
      name={name}
      control={control}
      errors={errors}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      onChangeValue={(val) => onChange(String(val ?? ""))}
      inputWidth="100%"
      inputWidthSp="100%"
      hasSpacing={false}
    />
  );
};
